import { format } from 'date-fns'
import puzzlesAPI from '../../../API/puzzlesConfig'
import { createQueryParams } from '../../../sharedComponents/src/globalHeader/common/queryParams'
import { errorHandler } from '../../../sharedComponents/src/globalHeader/functions/ams-functions'

export interface ITag {
    id: number
    name: string
    friendlyName: string
}

export enum PuzzleStatus {
    PASSED = 'passed',
    FAILED = 'failed',
    SKIPPED = 'skipped',
    SHOWED = 'solution_showed',
}

export interface IResolvePuzzle {
    puzzleId: string
    status: PuzzleStatus
    usedHints: number
    attempts: number
}

export interface FtueOptions {
    highlightHint: boolean
    highlightPiece: boolean
    showArrow: boolean
    highlightSecondHint: number
    highlightDescription: boolean
    highlightLegalMove: boolean
}

interface IPuzzleError {
    error?: any
}

interface Options {
    availableHints: number
}

type PracticeStreak = {
    best: number
    current: number
    updatedAt: string
}

interface IPuzzleData extends IPuzzleError {
    ftueOptions?: FtueOptions
    options?: Options
    puzzle?: {
        id: string
        startingFen: string
        moves: string
        rating: number
        tags: Array<ITag>
        description: string
        isFirstMove: boolean
    }
    streak?: PracticeStreak
    userPuzzleRating?: number
}

type DailyStreak = {
    currentStreak: number
    currentPuzzleResolved: boolean
}

export interface IDailyPuzzleData extends Omit<IPuzzleData, 'streak'> {
    date?: string
    dateForNextPuzzle?: string
    nextPuzzleAvailable?: boolean
    previousPuzzleAvailable?: boolean
    streak?: DailyStreak
    options?: Options
}

interface ITagsData extends IPuzzleError {
    tags?: Array<ITag>
}

interface IResolvePuzzleData extends IPuzzleError {
    ftueCompleted?: boolean
    result: boolean
    userPuzzleRating: number
}

interface IResolvePuzzlePracticeData extends IResolvePuzzleData {
    streak?: PracticeStreak
}

interface IResolveDailyPuzzleData extends IResolvePuzzleData {
    streak: DailyStreak
}

export const getPuzzleTags = async (): Promise<ITagsData> => {
    try {
        const response = await puzzlesAPI.get('/api/get_tags')
        const defaultData = { tags: [] }
        return response.data ? { tags: response.data.puzzleTags } : defaultData
    } catch (error) {
        return errorHandler(error)
    }
}

export const getPracticePuzzle = async (tags: Array<number>): Promise<IPuzzleData> => {
    try {
        const response = await puzzlesAPI.put('/api/get_puzzle', { puzzleTags: tags })
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const resolvePracticePuzzle = async (puzzle: IResolvePuzzle): Promise<IResolvePuzzlePracticeData> => {
    try {
        const response = await puzzlesAPI.put('/api/resolve_puzzle', puzzle)
        return response.data
    } catch (error) {
        return { result: false, userPuzzleRating: 0, error: errorHandler(error) }
    }
}

export const getDailyPuzzle = async (date: Date): Promise<IDailyPuzzleData> => {
    try {
        const response = await puzzlesAPI.get(
            `/api/daily/puzzles/next${createQueryParams({
                date: format(date, 'yyyy-MM-dd'),
            })}`,
        )
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const resolveDailyPuzzle = async (puzzle: IResolvePuzzle): Promise<IResolveDailyPuzzleData> => {
    try {
        const { puzzleId, ...restOfPuzzle } = puzzle
        const response = await puzzlesAPI.put(`/api/daily/puzzles/${puzzleId}/resolve`, restOfPuzzle)
        return { result: true, userPuzzleRating: response.data.userPuzzleRating, streak: response.data.streak }
    } catch (error) {
        return {
            result: false,
            userPuzzleRating: 0,
            streak: {
                currentStreak: 0,
                currentPuzzleResolved: false,
            },
            error: errorHandler(error),
        }
    }
}

export const usedHint = async () => {
    try {
        const response = await puzzlesAPI.put('/api/puzzles/hint')
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}
